import { FeatureFlags, decideFeatureFlag } from '~/utils/featureflags';
import { FullStoryEvent, fullStoryTracking } from '~/utils/fullstory/tracking';

import { type PrintEventProperties } from '../types/deviceEventCatalog.type';
import { type DeviceEvent } from '../types/deviceEvents.type';

/**
 * FullStory instrumentation for `device.print.*` events (FE-10.2 — Print Lifecycle Instrumentation).
 *
 * Every print lifecycle event funnels through the single device-events submitter callback
 * (see {@link file://./deviceEventsInit.util.ts}). Forwarding to FullStory here — rather than at
 * each of the five dispatch sites — guarantees FullStory telemetry derives from the identical
 * payload at the identical moment as the Kafka `device.print.*` events, so the two streams cannot
 * drift:
 *
 * - `device.print.submitted` → FullStory `Print Documents` (dispatch to QZ Tray)
 * - `device.print.completed` → FullStory `Print Job Result` (`result: 'success'`)
 * - `device.print.failed`    → FullStory `Print Job Result` (`result: 'failure'`)
 *
 * Gated by the Devices feature flag so all instrumentation is flag-controlled in one place.
 */

const isDevicesEnabled = (): boolean =>
  decideFeatureFlag(FeatureFlags.QUICKPACK_DEVICES)?.enabled ?? false;

const toPrintDocumentsProps = (properties: PrintEventProperties) => ({
  asset: properties.asset,
  correlation_id: properties.correlation_id,
  document_count: properties.document_count,
  entrypoint: properties.entrypoint,
  order_channel: properties.order_channel,
  printer_name: properties.printer_name,
});

const toPrintJobResultProps = (
  result: 'failure' | 'success',
  properties: PrintEventProperties,
) => ({
  asset: properties.asset,
  correlation_id: properties.correlation_id,
  document_count: properties.document_count,
  entrypoint: properties.entrypoint,
  error_code: properties.error_code,
  error_message: properties.error_message,
  order_channel: properties.order_channel,
  print_job_status_code: properties.print_job_status_code,
  printer_name: properties.printer_name,
  result,
  status: properties.status,
});

/**
 * Forwards a single `device.print.*` payload to FullStory when the Devices flag is on.
 * Best-effort: never throws, so a FullStory failure cannot disrupt the device-events pipeline.
 */
export const trackDeviceEventInFullStory = (event: DeviceEvent): void => {
  try {
    const { event: name } = event;

    // Cheap, allocation-free guard first: only `device.print.*` events are mirrored, so
    // skip the feature-flag lookup entirely for other (potentially high-volume) device events.
    if (
      name !== 'device.print.submitted' &&
      name !== 'device.print.completed' &&
      name !== 'device.print.failed'
    )
      return;

    if (!isDevicesEnabled()) return;

    const properties = ('properties' in event ? event.properties : undefined) as
      | PrintEventProperties
      | undefined;
    if (!properties) return;

    switch (name) {
      case 'device.print.submitted':
        fullStoryTracking(FullStoryEvent.PRINT_DOCUMENTS, toPrintDocumentsProps(properties));
        break;
      case 'device.print.completed':
        fullStoryTracking(
          FullStoryEvent.PRINT_JOB_RESULT,
          toPrintJobResultProps('success', properties),
        );
        break;
      case 'device.print.failed':
        fullStoryTracking(
          FullStoryEvent.PRINT_JOB_RESULT,
          toPrintJobResultProps('failure', properties),
        );
        break;
      default:
        break;
    }
  } catch {
    // FullStory forwarding is best-effort; swallow so device events are unaffected.
  }
};
